import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { LinearGradient } from 'expo-linear-gradient';
import { Loader2, RotateCcw, Sparkles, X } from 'lucide-react-native';
import React, { memo } from 'react';
import { Text, TouchableOpacity, View } from 'react-native';
import { useTheme } from 'styled-components/native';
import {
  useAIAssistantActions,
  useAIAssistantState,
} from '../AIAssistantProvider';
import { AiCreditBalanceLabel } from './ai-credits/AiCreditBalanceLabel';
import { useStyles } from './styles';

export const DragHandle = memo(function DragHandle() {
  const styles = useStyles();
  return (
    <View style={styles.dragHandleContainer}>
      <View style={styles.dragHandle} />
    </View>
  );
});

const GRADIENT_COLORS = ['#6366F1', '#3B82F6', '#06B6D4'] as const;

export const PanelHeader = memo(function PanelHeader({
  creditBalance,
}: {
  /** Null hides the balance (not loaded, no balance yet, or failed). */
  creditBalance: number | null;
}) {
  const theme = useTheme();
  const { rpx } = useResponsiveContext();
  const isMobile = useIsMobileLayout();
  const actions = useAIAssistantActions();
  const state = useAIAssistantState();
  const styles = useStyles();
  const accentColor = theme.link;

  const isGradient = !isMobile;
  const iconColor = isGradient ? '#fff' : accentColor;
  const titleColor = isGradient ? '#fff' : '#1E293B';
  const btnColor = isGradient ? 'rgba(255,255,255,0.7)' : '#94A3B8';
  const closeColor = isGradient ? 'rgba(255,255,255,0.85)' : '#64748B';

  const content = (
    <View style={[styles.header, isGradient && styles.headerGradient]}>
      <View style={styles.headerLeft}>
        <Sparkles color={iconColor} size={rpx(18)} strokeWidth={2.2} />
        <Text style={[styles.headerTitle, { color: titleColor }]}>AI 助手</Text>
        {state.isStreaming && (
          <View
            style={[
              styles.headerStreamingBadge,
              isGradient && { backgroundColor: 'rgba(255,255,255,0.2)' },
            ]}
          >
            <Loader2
              color={isGradient ? '#fff' : accentColor}
              size={rpx(10)}
              strokeWidth={2.5}
            />
            <Text
              style={[
                styles.headerStreamingText,
                { color: isGradient ? '#fff' : accentColor },
              ]}
            >
              回复中
            </Text>
          </View>
        )}
      </View>
      <View style={styles.headerRight}>
        {creditBalance != null ? (
          <AiCreditBalanceLabel balance={creditBalance} color={btnColor} />
        ) : null}
        {state.messages.length > 0 && (
          <TouchableOpacity
            onPress={() => void actions.clearSession()}
            hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
            style={styles.headerBtn}
          >
            <RotateCcw color={btnColor} size={rpx(16)} strokeWidth={2} />
          </TouchableOpacity>
        )}
        <TouchableOpacity
          onPress={actions.close}
          hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
          style={styles.headerBtn}
        >
          <X color={closeColor} size={rpx(18)} strokeWidth={2} />
        </TouchableOpacity>
      </View>
    </View>
  );

  if (!isGradient) return content;

  return (
    <LinearGradient
      colors={GRADIENT_COLORS as unknown as string[]}
      start={{ x: 0, y: 0.5 }}
      end={{ x: 1, y: 0.5 }}
      style={styles.headerGradientBg}
    >
      {content}
    </LinearGradient>
  );
});
